/**
 * Helpers for FolderSharing factory methods
 */

import type { IBaseNotary } from "../../BaseNotaryFactory";
import type { FolderSharing } from "le-coffre-resources/dist/Notary";
import {
	buildEmptyPayload,
	buildFolderSharingPayload,
	buildInvitedNotaryEmailPayload,
} from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";

export interface ShareFolderParams {
	invitedNotaryEmail: string;
	invitedNotaryCrpcen?: string;
	canViewOtherMembersDocuments?: boolean;
	invitedNotaryIdnot?: string;
	invitedNotaryOfficeIdNot?: string;
	invitedNotaryFirstName?: string;
	invitedNotaryLastName?: string;
	invitedNotaryContactUid?: string;
}

/** API may include invitation email outcome alongside FolderSharing fields. */
export type FolderSharingShareResponse = FolderSharing & {
	invitation_email?: { sent: boolean; message?: string; code?: string };
};

/**
 * Create share folder method
 */
export function createShareFolderMethod(baseNotary: IBaseNotary, foldersUrl: string) {
	return async (folderUid: string, params: ShareFolderParams): Promise<FolderSharingShareResponse> => {
		const url = new URL(foldersUrl.concat(`/${folderUid}/share`));
		try {
			const payload = buildFolderSharingPayload(params);
			return await baseNotary.postRequest<FolderSharingShareResponse>(url, payload);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create get shared folders method
 */
export function createGetSharedFoldersMethod(baseNotary: IBaseNotary) {
	return async (): Promise<FolderSharing[]> => {
		const url = new URL(baseNotary.namespaceUrl.concat("/folders/shared"));
		try {
			return await baseNotary.getRequest<FolderSharing[]>(url);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create get folder shares method
 */
export function createGetFolderSharesMethod(baseNotary: IBaseNotary, foldersUrl: string) {
	return async (folderUid: string): Promise<FolderSharing[]> => {
		const url = new URL(foldersUrl.concat(`/${folderUid}/shares`));
		try {
			return await baseNotary.getRequest<FolderSharing[]>(url);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create resend invitation method
 */
export function createResendInvitationMethod(baseNotary: IBaseNotary, foldersUrl: string) {
	return async (folderUid: string, shareUid: string): Promise<void> => {
		const url = new URL(foldersUrl.concat(`/${folderUid}/shares/${shareUid}/resend-invitation`));
		try {
			return await baseNotary.postRequest<void>(url, buildEmptyPayload());
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create revoke share method
 */
export function createRevokeShareMethod(baseNotary: IBaseNotary, foldersUrl: string) {
	return async (folderUid: string, shareUid: string): Promise<FolderSharing> => {
		const url = new URL(foldersUrl.concat(`/${folderUid}/shares/${shareUid}/revoke`));
		try {
			return await baseNotary.postRequest<FolderSharing>(url, buildEmptyPayload());
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create update share method
 */
export function createUpdateShareMethod(baseNotary: IBaseNotary, foldersUrl: string) {
	return async (
		folderUid: string,
		shareUid: string,
		invitedNotaryEmail: string,
		invitedNotaryIdnot?: string,
	): Promise<FolderSharing> => {
		const url = new URL(foldersUrl.concat(`/${folderUid}/shares/${shareUid}`));
		try {
			const payload = buildInvitedNotaryEmailPayload(invitedNotaryEmail, invitedNotaryIdnot);
			return await baseNotary.putRequest<FolderSharing>(url, payload);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}
